import React from 'react';
import { Upload, Info } from 'lucide-react';

interface WelcomeModalProps {
  isOpen: boolean;
  onClose: () => void;
  onUpload: () => void;
  onDemo: () => void;
  hasDemoSession: boolean;
  isLoading?: boolean;
}

const WelcomeModal: React.FC<WelcomeModalProps> = ({
  isOpen,
  onClose,
  onUpload,
  onDemo,
  hasDemoSession,
  isLoading = false,
}) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-8 max-w-2xl mx-4 shadow-xl">
        {/* Header */}
        <div className="text-center mb-6">
          <div className="w-24 h-24 bg-rowing-sky bg-opacity-10 rounded-full flex items-center justify-center mx-auto mb-6">
            <svg
              className="w-12 h-12 text-rowing-blue"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
              />
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-rowing-navy mb-3 font-display">
            Welcome to GLYYD Coach Portal
          </h2>
          <p className="text-rowing-gray-600 mb-8">
            Get started by uploading your first telemetry session. Upload Peach PowerLine files to
            analyze crew performance and gain insights into your team's technique.
          </p>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 mb-6">
          <button
            onClick={onUpload}
            className="w-full sm:flex-1 btn-primary flex items-center justify-center space-x-3 py-3"
            disabled={isLoading}
          >
            <Upload className="w-5 h-5" />
            <span>Upload First Session</span>
          </button>

          {hasDemoSession && (
            <button
              onClick={onDemo}
              className="w-full sm:flex-1 btn-secondary flex items-center justify-center space-x-3 py-3"
              disabled={isLoading}
            >
              <Info className="w-5 h-5" />
              <span>Continue with Demo</span>
            </button>
          )}
        </div>

        {/* Loading indicator */}
        {isLoading && (
          <div className="text-center mb-4">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-rowing-blue mx-auto mb-2"></div>
            <p className="text-sm text-rowing-gray-500">Loading demo data...</p>
          </div>
        )}

        {/* Supported Formats Info */}
        <div className="text-sm text-rowing-gray-500 bg-rowing-gray-50 px-4 py-3 rounded-lg border border-rowing-gray-200">
          <p className="font-medium mb-1">Supported formats:</p>
          <p>Peach Systems (.xlsx files)</p>
          <p>Maximum file size: 10MB</p>
        </div>

        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 p-2"
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </button>
      </div>
    </div>
  );
};

export default WelcomeModal;
